// ---------------------------------------------------------------------------
// Matching a path to a route, and building one (R12c)
//
// Pure functions over the route table (routes.ts); nothing here touches the
// DOM. Two of today's bugs are fixed here:
//
//   B2  @adornis/router matched the raw first path, so a trailing slash on
//       load (/optout/?email=…, /settings/) matched nothing and the opt-out
//       did not happen. normalizePath drops it.
//   B14 params arrived URL-encoded, so /campaign/%20/edit reached the page
//       as "%20" and its "Invalid campaign ID." view could not be reached.
//       They arrive decoded; a malformed escape is no match.
//
// /api/* and /auth/* never match: the server answers them (the OAuth
// callbacks are server pages, server/startup.ts:247-292), and a link to one
// must stay a full page load.
// ---------------------------------------------------------------------------

import { ROUTES, type RouteTable } from './routes.js';

export type Params = Readonly<Record<string, string>>;

export interface Match<Name extends string = string> {
  name: Name;
  params: Params;
}

/** The path without trailing slashes ('/' stays '/'). Case and inner slashes are kept. */
export function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** True for the paths the server answers, which no route may claim. */
export function isReservedPath(path: string): boolean {
  return /^\/(api|auth)(\/|$)/.test(path);
}

/** The route a path names, with its params decoded; null when none does. */
export function matchRoute<T extends RouteTable = typeof ROUTES>(
  pathname: string,
  routes: T = ROUTES as unknown as T,
): Match<Extract<keyof T, string>> | null {
  const path = normalizePath(pathname);
  if (isReservedPath(path)) return null;
  const segments = path.split('/');
  for (const [name, route] of Object.entries(routes)) {
    const pattern = route.path.split('/');
    if (pattern.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let matched = true;
    for (let i = 0; i < pattern.length && matched; i++) {
      const want = pattern[i] as string;
      const have = segments[i] as string;
      if (want.startsWith(':')) {
        if (have === '') matched = false;
        else {
          try {
            params[want.slice(1)] = decodeURIComponent(have);
          } catch {
            matched = false;
          }
        }
      } else if (want !== have) matched = false;
    }
    if (matched) return { name: name as Extract<keyof T, string>, params };
  }
  return null;
}

/**
 * The path of a route with these params, encoded; null when a param the path needs is missing,
 * empty or blank. Nothing is taken from the current route: today's goTo filled missing params from
 * it (make-router.js:18-20, 81-92), and the wizard's "Back to Campaign" passes draft._id, which can
 * be unset — that built /campaign//detail.
 */
export function pathFor<T extends RouteTable = typeof ROUTES>(
  name: Extract<keyof T, string>,
  params: Readonly<Record<string, string | null | undefined>> = {},
  routes: T = ROUTES as unknown as T,
): string | null {
  const route = routes[name];
  if (!route) return null;
  const out: string[] = [];
  for (const segment of route.path.split('/')) {
    if (!segment.startsWith(':')) {
      out.push(segment);
      continue;
    }
    const value = params[segment.slice(1)];
    if (typeof value !== 'string' || value.trim() === '') return null;
    out.push(encodeURIComponent(value));
  }
  return out.join('/') || '/';
}
